import { useEffect, useState } from 'react'
import { ChevronDown } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card.tsx'
import LoadingSpinner from '../../components/ui/LoadingSpinner.tsx'
import { apiGet } from '../../lib/api.ts'

interface Placement {
  id: number
  title: string
  start_date: string
  shift_start_time: string
  shift_end_time: string
  location_city: string
  job_status: string
  placement_status: string
  first_name: string
  last_name: string
  headline: string
  average_rating: number | null
  review_count: number
}

interface GroupedJob {
  id: number
  title: string
  start_date: string
  shift_start_time: string
  shift_end_time: string
  location_city: string
  candidates: Placement[]
}

export default function CandidatesPage() {
  const [jobs, setJobs] = useState<GroupedJob[]>([])
  const [expandedJobIds, setExpandedJobIds] = useState<number[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    const load = async () => {
      try {
        const data = await apiGet<{ pending_placements: Placement[]; confirmed_placements: Placement[] }>(
          '/practice/bookings.php?format=json'
        )

        const groupedJobs = groupCandidatesByJob([
          ...data.pending_placements,
          ...data.confirmed_placements,
        ])

        setJobs(groupedJobs)
        setExpandedJobIds([])
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Unable to load candidate activity.')
      } finally {
        setLoading(false)
      }
    }

    void load()
  }, [])

  const toggleJob = (jobId: number) => {
    setExpandedJobIds((current) =>
      current.includes(jobId)
        ? current.filter((id) => id !== jobId)
        : [...current, jobId]
    )
  }

  if (loading) {
    return <LoadingSpinner />
  }

  return (
    <div className="p-4 space-y-4">
      <div>
        <h1 className="text-2xl font-bold text-text-900">Candidates</h1>
        <p className="mt-1 text-sm text-text-600">Applicants are grouped by the job posting they applied for.</p>
      </div>

      {error && <div className="rounded-lg bg-red-50 p-3 text-sm text-red-700">{error}</div>}

      <Card>
        <CardHeader>
          <CardTitle>Applications by job</CardTitle>
        </CardHeader>
        <CardContent>
          {jobs.length === 0 ? (
            <p className="text-text-700">No candidate activity yet.</p>
          ) : (
            <div className="space-y-3">
              {jobs.map((job) => {
                const isExpanded = expandedJobIds.includes(job.id)

                return (
                  <div key={job.id} className="overflow-hidden rounded-xl border border-surface-300">
                    <button
                      type="button"
                      className="flex w-full items-center justify-between gap-4 bg-white px-4 py-4 text-left"
                      onClick={() => toggleJob(job.id)}
                    >
                      <div>
                        <h3 className="font-semibold text-text-900">{job.title}</h3>
                        <p className="mt-1 text-sm text-text-600">
                          {formatJobDate(job.start_date, job.shift_start_time, job.shift_end_time)}
                        </p>
                        {job.location_city && (
                          <p className="mt-1 text-xs uppercase tracking-[0.14em] text-text-500">
                            {job.location_city}
                          </p>
                        )}
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="rounded-full bg-brand-50 px-3 py-1 text-xs font-semibold text-brand-700">
                          {job.candidates.length} candidate{job.candidates.length === 1 ? '' : 's'}
                        </span>
                        <ChevronDown
                          size={20}
                          className={`text-text-500 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                        />
                      </div>
                    </button>

                    {isExpanded && (
                      <div className="border-t border-surface-300 bg-slate-50 px-4 py-3">
                        <div className="space-y-3">
                          {job.candidates.map((candidate, index) => (
                            <div key={`${job.id}-${candidate.first_name}-${candidate.last_name}-${index}`} className="rounded-lg bg-white p-4">
                              <p className="font-semibold text-text-900">
                                {[candidate.first_name, candidate.last_name].filter(Boolean).join(' ') || 'Applicant'}
                              </p>
                              <p className="mt-1 text-sm text-text-600">
                                {candidate.headline || 'No profile headline added yet.'}
                              </p>
                              <div className="mt-3 flex items-center gap-3">
                                <ReviewStars average={candidate.average_rating} />
                                <span className="text-sm font-semibold text-text-700">
                                  Avg {(candidate.average_rating ?? 0).toFixed(1)}
                                </span>
                                <span className="text-xs text-text-500">
                                  ({candidate.review_count} review{candidate.review_count === 1 ? '' : 's'})
                                </span>
                              </div>
                              <p className="mt-2 text-xs font-medium uppercase tracking-[0.14em] text-text-500">
                                {candidate.placement_status || candidate.job_status}
                              </p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

function groupCandidatesByJob(placements: Placement[]): GroupedJob[] {
  const jobs = new Map<number, GroupedJob>()

  placements.forEach((placement) => {
    const candidateName = [placement.first_name, placement.last_name].filter(Boolean).join(' ').trim()
    const hasCandidate = candidateName !== '' || placement.headline.trim() !== '' || placement.placement_status.trim() !== ''

    if (!hasCandidate) {
      return
    }

    const existing = jobs.get(placement.id)
    if (existing) {
      existing.candidates.push(placement)
      return
    }

    jobs.set(placement.id, {
      id: placement.id,
      title: placement.title,
      start_date: placement.start_date,
      shift_start_time: placement.shift_start_time,
      shift_end_time: placement.shift_end_time,
      location_city: placement.location_city,
      candidates: [placement],
    })
  })

  return Array.from(jobs.values())
}

function formatJobDate(startDate: string, startTime: string, endTime: string): string {
  const parts = [startDate]

  if (startTime && endTime) {
    parts.push(`${startTime} - ${endTime}`)
  }

  return parts.filter(Boolean).join(' · ')
}

function ReviewStars({ average }: { average: number | null }) {
  const fill = Math.max(0, Math.min(100, ((average ?? 0) / 5) * 100))

  return (
    <span className="relative inline-block text-sm leading-none tracking-[0.12rem]" aria-hidden="true">
      <span className="text-slate-300">★★★★★</span>
      <span
        className="absolute left-0 top-0 overflow-hidden whitespace-nowrap text-amber-400"
        style={{ width: `${fill}%` }}
      >
        ★★★★★
      </span>
    </span>
  )
}
